/**
*****************************************
*                                       *
* NAGARRO  - CSX - CONFIDENTIAL         *
*                                       *
*****************************************
*/



import { AllocDeallocData } from './../model/alloc-dealloc-data.model';
import { AppUtilityService } from '../../../core/services/app-utility.service';
import { ConfigService } from '../../../core/services/config.service';
import { DataStoreService } from '../../../core/services/datastore.service';
import { Injectable } from '@angular/core';
import { LmsLoaderService } from './../../../core/services/lms-loader.service';
import { LmsLoggerService } from '../../../core/services/lms-logger.service';
import { LocoTransfer } from '../../../core/model/loco-transfer.model';
import { MessageHandlerService } from '../../../core/services/message-handler.service';
import { Observable } from 'rxjs';
import { PopupService } from '../../../core/services/popup.service';
import { Router } from '@angular/router';
import { StaticDataStoreService } from './../../../core/services/static-datastore.service';
import { YardAndLocalRequest } from '../model/yard-local-request.model';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { tap } from 'rxjs/operators';

/**
 * Loco Status Service to get data for locomotives details.
 * @export
 * @class LocoStatusService
 */
@Injectable()
export class YardLocalService {

    private RESOURCE_URL = '/yardandlocal';
    private configData: any;
    private apiUrl: string;
    private WINDOW_CODE = 'LWYL';
    private ALLOC_STATION_URL = '/stations';
    private ADD_ALLOC_STATION_URL = '/add';
    private UPDATE_ALLOC_STATION_URL = '/update';
    private DELETE_ALLOC_STATION_URL = '/delete';

    /**
     * Creates an instance of LocoStatusService.
     * @param {HttpClient} http
     * @param {ConfigService} configService
     * @param {DataStoreService} dataStoreService
     * @param {LmsLoggerService} lmsLoggerService
     * @memberOf LocoStatusService
     */
    constructor(private http: HttpClient,
        private configService: ConfigService,
        private dataStoreService: DataStoreService,
        private lmsLoggerService: LmsLoggerService,
        private router: Router,
        private lmsLoaderService: LmsLoaderService, private messageHandlerService: MessageHandlerService, private popupService: PopupService,
        private appUtilityService: AppUtilityService,
        private staticDataStoreService: StaticDataStoreService) {
        this.configData = this.configService.getConfigData();
        this.apiUrl = this.configData.urls.API_URL;
    }

    load(locos) {
        this.lmsLoggerService.logInfo('LocoDataService => load()',
            `Loco Status Fetch Successfull for ${JSON.stringify(locos)}`);
    }
    /**
     * Fetching Data from Rest Service Provided or loading data from dataStoreService if the data is already present.
     * @param {Tab} tab
     * @param {boolean} isRefresh
     * @memberOf LocoStatusService
     */
    getData(division: string, allocStation: string, stTypeCode: string, includeSpAlloc: string) {
        this.lmsLoaderService.showLoader();
        this.lmsLoggerService.logInfo('YardLocalService=>getData service call starts');
        if(allocStation && allocStation === 'ALL'){
            allocStation = 'all';
        }
        return this.http.get<any>(
            `${this.configService.getConfigData().urls.API_URL}${this.RESOURCE_URL}?oprtngZoneCode=${division}&st=${allocStation}&stTypeCode=${stTypeCode}&includeSpAlloc=${includeSpAlloc}`)
            // return this.http.get('data/yardAndLocal.json')
            .pipe(
              tap(() => this.lmsLoggerService.logInfo('YardLocalService=>getData service call ends'))
            );
    }

    /**
     * Service call for de-allocation of locotmotives.
     * @param {AllocDeallocData} allocDeallocData
     * @returns {Observable<Response>}
     * @memberof YardLocalService
     */
    deAllocateLocomotives(allocDeallocData: AllocDeallocData): Observable<Response> {
        let headers = new HttpHeaders();
        const apiUrl = this.configData.urls.API_URL;
        const functionCode = 'DEAL';
        headers = headers.append('Content-Type', 'application/json');
        const options = { headers: headers };
        this.lmsLoggerService.logInfo('YardLocalService=>deAllocateLocomotives service call starts');
        return this.http.post<any>(`${apiUrl}/locomotives/deal?opr=${functionCode}&wc=${this.WINDOW_CODE}`,
            allocDeallocData, options).pipe(
              tap(() => this.lmsLoggerService.logInfo('YardLocalService=>deAllocateLocomotives service call ends'))
            );
    }

    /**
     * Service call for allocate locomotives.
     * @param {AllocDeallocData} allocDeallocData
     * @returns {Observable<Response>}
     * @memberof YardLocalService
     */
    allocateLocomotives(allocDeallocData: AllocDeallocData): Observable<Response> {
        let headers = new HttpHeaders();
        const apiUrl = this.configData.urls.API_URL;
        const functionCode = 'ALOC';
        headers = headers.append('Content-Type', 'application/json');
        const options = { headers: headers };
        this.lmsLoggerService.logInfo('YardLocalService=>allocateLocomotives service call starts');
        return this.http.post<any>(`${apiUrl}/locomotives/aloc?opr=${functionCode}&wc=${this.WINDOW_CODE}`,
            allocDeallocData, options).pipe(
              tap(() => this.lmsLoggerService.logInfo('YardLocalService=>allocateLocomotives service call ends'))
            );
    }

    /**
     * Loco Transfer Service Call
     * @param {Array<LocoTransfer>} locoTransfer
     * @param {string} operation
     * @returns {Observable<Response>}
     * @memberOf LocoStatusService
     */
    transferLocomotive(locoTransfer: Array<LocoTransfer>, operation: string): Observable<Response> {
        let headers = new HttpHeaders();
        const apiUrl = this.configData.urls.API_URL;
        const windowCode = 'LWYL'; // changes for yard and local screen
        headers = headers.append('Content-Type', 'application/json');
        const options = { headers: headers };
        this.lmsLoggerService.logInfo('YardLocalService=>transferLocomotive service call starts');
        return this.http.put<any>(`${apiUrl}/locomotives?opr=${operation}&wc=${windowCode}`,
            JSON.stringify(locoTransfer), options).pipe(
              tap(() => this.lmsLoggerService.logInfo('YardLocalService=>transferLocomotive service call ends'))
            );
    }

    /**
     * Service request for bre exception.
     * @param {Array<String>} locoList
     * @param {string} trainId
     * @returns {Observable<Response>}
     * @memberOf LocoStatusService
     */
    breTransferRequest(locoList: Array<String>, trainId: string): Observable<Response> {
        const apiUrl = this.configData.urls.API_URL;
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        const options = { headers: headers };
        this.lmsLoggerService.logInfo('YardLocalService=>breTransferRequest service call starts');
        return this.http.put<any>(`${apiUrl}/trains/${trainId}/breparam?opr=INQU`,
            JSON.stringify(locoList), options).pipe(
              tap(() => this.lmsLoggerService.logInfo('YardLocalService=>breTransferRequest service call ends'))
            );
    }

    /**
     * Get allocation station.
     * @param {string} division
     * @param {string} allocStation
     * @param {string} operation
     * @returns
     * @memberof YardLocalService
     */
    getAllocationStations(division: string, allocStation: string) {
        if (division) { division = division.toUpperCase(); }
        if (allocStation) { allocStation = allocStation.toUpperCase(); }

        this.lmsLoaderService.showLoader();
        this.lmsLoggerService.logInfo('YardLocalService=>getAllocationStations service call starts');
        return this.http.get<any>(
            `${this.configService.getConfigData().urls.API_URL}${this.RESOURCE_URL}${this.ALLOC_STATION_URL}?div=${division}&st=${allocStation}&opr=INQS`)
            .pipe(
              tap(() => this.lmsLoggerService.logInfo('YardLocalService=>getAllocationStations service call ends'))
            );
    }

    /**
     * Add allocation station.
     * @param {string} division
     * @param {string} allocStation
     * @param {string} operation
     * @returns
     * @memberof YardLocalService
     */
    addAllocationStation(yardAndLocalRequest: YardAndLocalRequest) {
        const apiUrl = this.configData.urls.API_URL;
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        const options = { headers: headers };
        this.lmsLoggerService.logInfo('YardLocalService=>addAllocationStation service call starts');
        return this.http.post<any>(`${apiUrl}${this.RESOURCE_URL}${this.ADD_ALLOC_STATION_URL}?opr=SADD`,
            JSON.stringify(yardAndLocalRequest), options).pipe(
              tap(() => this.lmsLoggerService.logInfo('YardLocalService=>addAllocationStation service call ends'))
            );
    }

    /**
    * Update allocation station.
    * @param {string} division
    * @param {string} allocStation
    * @param {string} operation
    * @returns
    * @memberof YardLocalService
    */
    updateAllocationStation(yardAndLocalRequest: YardAndLocalRequest) {
        const apiUrl = this.configData.urls.API_URL;
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        const options = { headers: headers };
        this.lmsLoggerService.logInfo('YardLocalService=>updateAllocationStation service call starts');
        return this.http.put<any>(`${apiUrl}${this.RESOURCE_URL}${this.UPDATE_ALLOC_STATION_URL}?opr=UPDT`,
            JSON.stringify(yardAndLocalRequest), options).pipe(
              tap(() => this.lmsLoggerService.logInfo('YardLocalService=>updateAllocationStation service call ends'))
            );
    }

    /**
     * Delete allocation station.
     * @param {string} division
     * @param {string} allocStation
     * @param {string} operation
     * @returns
     * @memberof YardLocalService
     */
    deleteAllocationStation(division: string, allocationStation: string) {
        const apiUrl = this.configData.urls.API_URL;
        this.lmsLoggerService.logInfo('YardLocalService=>deleteAllocationStation service call starts');
        return this.http.delete<any>(`${apiUrl}${this.RESOURCE_URL}${this.DELETE_ALLOC_STATION_URL}?opr=DLET&div=${division}&st=${allocationStation}`).pipe(
          tap(() => this.lmsLoggerService.logInfo('YardLocalService=>deleteAllocationStation service call ends'))
        );
    }

    /**
     * @memberOf LocoStatusService
     */
    cleanBehaviourSubject() {

    }
}
